import React, { CSSProperties, useContext } from "react";
import CustomButton from "../../../components/Button/CustomButton";
import TestRequestApi from "../../../api/services/TestRequestApi";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { tesaBlue } from "../../../styles/colors";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import useEnv from "../../../hooks/env";
import { ConfirmationDialogParams } from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialog";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import TestRequestLifecycleApi from "../../../api/services/TestRequestLifecycleApi";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import TestInformationApi from "../../../api/services/TestInformationApi";

const COLOR_PRIMARY = tesaBlue;

export interface AcceptTestRequestButtonProps {
  disabled?: boolean;
  approvalNeeded: boolean;
  testRequestId: string;
  testRequestName: string;
  duplicatedFrom: string | null;
  onSuccess: (testRequest: TestRequestResponse) => void;
  preSave: () => Promise<void | TestRequestResponse>;
  style: CSSProperties;
}

const AcceptTestRequestButton: React.FC<AcceptTestRequestButtonProps> = (props) => {
  const { approvalNeeded, disabled, duplicatedFrom, onSuccess, preSave, testRequestName, style, testRequestId } = props;
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const testInformationApi = new TestInformationApi(api);
  const testRequestLifecycleApi = new TestRequestLifecycleApi(api);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { reuseSamplesAndTestSeriesOfDuplicate } = useContext(TestRequestPermissionContext);

  const DIALOG_PARAMS_REGULAR: ConfirmationDialogParams = {
    text: (
      <>
        Accept test request: <b>{testRequestName}</b> and notify requester
      </>
    ),
    confirmText: "Accept",
    confirmColor: COLOR_PRIMARY,
  };

  const DIALOG_PARAMS_DUPLICATE = (hint: string | boolean): ConfirmationDialogParams => ({
    headline: "USE INFORMATION FROM ORIGINAL TEST REQUEST",
    text: (
      <>
        <p>
          This is a duplicated test request. You can reuse the exact same samples from the original test request or
          duplicate the samples to create new samples with similar information.
        </p>
        {hint && <p>Hint: {hint}</p>}
      </>
    ),
    confirmText: "Duplicate",
    confirmColor: COLOR_PRIMARY,
    additionalAction: {
      id: "USE_EXISTING",
      text: "Use Existing",
    },
  });

  const DIALOG_PARAMS_APPROVAL = {
    headline: "SEND FOR APPROVAL",
    text: <>Since an approver is entered the test request needs to be sent for Approval first.</>,
    confirmText: "Send for Approval",
    confirmColor: COLOR_PRIMARY,
  };

  const accept = (): Promise<TestRequestResponse> => {
    return testRequestLifecycleApi.acceptTestRequest(testRequestId).then((response) => response.data);
  };

  const handleDuplicateConfirmation = (actionId: string): Promise<TestRequestResponse> => {
    if (actionId === "USE_EXISTING") {
      return testRequestApi.reuseSamplesAndTestPlans(testRequestId).then((response) => response.data);
    }
    return testRequestApi.copySamplesAndTestPlans(testRequestId).then((response) => response.data);
  };

  const onSubmit = () => {
    if (approvalNeeded) {
      showConfirmationDialog(DIALOG_PARAMS_APPROVAL)
        .then(() => preSave())
        .then(() => testRequestLifecycleApi.sendForApproval(testRequestId))
        .then(({ data }) => onSuccess(data))
        .catch(handleHttpError);
    } else if (reuseSamplesAndTestSeriesOfDuplicate && duplicatedFrom) {
      testInformationApi
        .fetchTestInformation(duplicatedFrom)
        .then((duplicatedFromTestInfo) =>
          Boolean(
            duplicatedFromTestInfo.data.testPlans
              ?.flatMap((testPlan) => testPlan.testSeriesSearchResults || [])
              .filter((testSeries) => Boolean(testSeries.linkedParent))?.length,
          ),
        )
        .then((showAdditionalText) =>
          showConfirmationDialog(
            DIALOG_PARAMS_DUPLICATE(
              showAdditionalText && "Test series with regained samples are not duplicated or reused.",
            ),
          ),
        )
        .then((actionId) => preSave().then(() => actionId))
        .then(handleDuplicateConfirmation)
        .then(onSuccess)
        .catch(handleHttpError);
    } else {
      showConfirmationDialog(DIALOG_PARAMS_REGULAR).then(preSave).then(accept).then(onSuccess).catch(handleHttpError);
    }
  };

  return <CustomButton disabled={disabled} onClick={onSubmit} primary text="Accept" style={style} />;
};

export default AcceptTestRequestButton;
